import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { api } from "@/lib/api-client"
import { queryKeys } from "@/lib/query-keys"

import { useAIParserSession } from "./use-ai-parser-session"

type UpdateCellData = {
  filename: string
  columnId: string
  value: string
}

function updateCell(sessionId: string, data: UpdateCellData) {
  return api.put("/parser/update-cell", {
    session_id: sessionId,
    filename: data.filename,
    column_id: data.columnId,
    value: data.value,
  })
}

export function useUpdateCellMutation() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { currentSessionId } = useAIParserSession()

  return useMutation({
    mutationFn: (data: UpdateCellData) => {
      if (!currentSessionId) {
        throw new Error("No session id")
      }
      return updateCell(currentSessionId, data)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.fullSession(currentSessionId ?? ""),
      })
      toast.success(t("pages.ai_parser.toasts.cell_updated"))
    },
    onError: () => {
      toast.error(t("pages.ai_parser.toasts.cell_update_failed"))
    },
  })
}
